import React, { createContext, useState, useContext, useEffect } from 'react';
import { authAPI } from '../services/api';
import api from '../services/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    const savedUser = localStorage.getItem('user');
    if (savedUser) {
      try {
        return JSON.parse(savedUser);
      } catch {
        return null;
      }
    }
    return null;
  });
  const [loading, setLoading] = useState(true);

  // Verifier le token au chargement en appelant l'API
  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');

    if (token && savedUser) {
      // Verifier que le token est encore valide
      api.get('/auth/me')
        .then(res => {
          setUser(res.data.user || JSON.parse(savedUser));
          setLoading(false);
        })
        .catch(() => {
          // Token expire ou invalide -> deconnexion
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          localStorage.removeItem('workspaceContext');
          setUser(null);
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, []);

  // Intercepteur axios : auto-logout sur 401
  useEffect(() => {
    const interceptor = api.interceptors.response.use(
      response => response,
      error => {
        if (error.response?.status === 401) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          localStorage.removeItem('workspaceContext');
          setUser(null);
        }
        return Promise.reject(error);
      }
    );
    return () => api.interceptors.response.eject(interceptor);
  }, []);

  const login = async (credentials) => {
    try {
      const response = await authAPI.login(credentials);
      const { token, user: userData } = response.data;
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(userData));
      // Flag session active : permet a l'app de rester connectee tant que l'onglet est ouvert
      // Efface automatiquement quand le navigateur est ferme -> force un login fresh
      sessionStorage.setItem('session_active', '1');
      setUser(userData);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Erreur de connexion'
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('workspaceContext');
    sessionStorage.removeItem('session_active');
    setUser(null);
  };

  const isAdmin = () => user?.role === 'admin';
  const isBoutique = () => user?.role === 'boutique';
  const isAtelier = () => user?.role === 'atelier';

  return (
    <AuthContext.Provider value={{
      user,
      login,
      logout,
      loading,
      isAdmin,
      isBoutique,
      isAtelier
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
